import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../lib/auth';
import { Link } from 'react-router-dom';
import api, { BACKEND_URL } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, User, Clock, ArrowRight, Plus, Heart, Edit, X, Save, Upload, Trash2, Download, File, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS = {
  new: 'Nouveau', qualified: 'Qualifié', in_study: "En étude", proposal_sent: 'Proposition envoyée',
  confirmed: 'Confirmé', coordinating: 'Coordination', completed: 'Terminé', closed: 'Clôturé'
};
const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700', qualified: 'bg-indigo-100 text-indigo-700',
  in_study: 'bg-yellow-100 text-yellow-700', proposal_sent: 'bg-purple-100 text-purple-700',
  confirmed: 'bg-green-100 text-green-700', coordinating: 'bg-teal-100 text-teal-700',
  completed: 'bg-emerald-100 text-emerald-700', closed: 'bg-gray-100 text-gray-600'
};

export default function CaregiverDashboard() {
  const { user, logout, updateProfile } = useAuth();
  const [requests, setRequests] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [editingProfile, setEditingProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '', phone: '' });
  const fileInputRef = useRef(null);

  const loadData = () => {
    api.get('/stay-requests/mine').then(r => setRequests(r.data)).catch(() => {});
    api.get('/documents/mine').then(r => setDocuments(r.data)).catch(() => {});
    api.get('/bookings').then(r => setBookings(r.data)).catch(() => {});
  };

  useEffect(() => { loadData(); }, []);

  const openEdit = () => {
    setProfileForm({ first_name: user?.first_name || '', last_name: user?.last_name || '', phone: user?.phone || '' });
    setEditingProfile(true);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profileForm.first_name.trim() || !profileForm.last_name.trim()) {
      toast.error('Prénom et nom sont obligatoires'); return;
    }
    setSaving(true);
    try {
      await updateProfile(profileForm);
      toast.success('Profil mis à jour');
      setEditingProfile(false);
    } catch { toast.error('Erreur lors de la mise à jour'); }
    finally { setSaving(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Fichier trop grand (max 10 Mo)'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${file.name} téléversé avec succès`);
      loadData();
    } catch { toast.error('Erreur lors du téléversement'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const deleteDocument = async (docId, name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Document supprimé');
      setDocuments(d => d.filter(x => x.id !== docId));
    } catch { toast.error('Erreur'); }
  };

  const downloadDoc = (docId) => {
    window.open(`${BACKEND_URL}/api/documents/${docId}/download`, '_blank');
  };

  const formatSize = (bytes) => bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} Ko` : `${(bytes / 1024 / 1024).toFixed(1)} Mo`;

  return (
    <div className="min-h-screen bg-background py-24 md:py-28" data-testid="caregiver-dashboard">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">Bonjour {user?.first_name}</h1>
            <p className="text-muted-foreground mt-1">Votre espace personnel</p>
          </div>
          <Link to="/demander-un-sejour">
            <Button className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="new-stay-request-btn">
              <Plus className="mr-2 w-4 h-4" /> Nouvelle demande
            </Button>
          </Link>
        </div>

        {/* Profile card */}
        <div className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm mb-6" data-testid="profile-card">
          {!editingProfile ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-lg">{user?.first_name} {user?.last_name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  {user?.phone && <p className="text-sm text-muted-foreground">{user?.phone}</p>}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={openEdit} className="rounded-xl" data-testid="edit-profile-btn">
                <Edit className="w-4 h-4 mr-2" /> Modifier
              </Button>
            </div>
          ) : (
            <form onSubmit={saveProfile} data-testid="profile-edit-form">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium">Modifier le profil</h3>
                <button type="button" onClick={() => setEditingProfile(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Prénom</Label>
                  <Input value={profileForm.first_name} onChange={e => setProfileForm(p => ({ ...p, first_name: e.target.value }))} className="mt-1 rounded-xl" required data-testid="profile-firstname-input" />
                </div>
                <div>
                  <Label>Nom</Label>
                  <Input value={profileForm.last_name} onChange={e => setProfileForm(p => ({ ...p, last_name: e.target.value }))} className="mt-1 rounded-xl" required data-testid="profile-lastname-input" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Téléphone</Label>
                  <Input type="tel" value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} className="mt-1 rounded-xl" data-testid="profile-phone-input" />
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <Button type="submit" disabled={saving} className="rounded-full" data-testid="profile-save-btn">
                  <Save className="w-4 h-4 mr-2" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditingProfile(false)} className="rounded-full">Annuler</Button>
              </div>
            </form>
          )}
        </div>

        {/* Documents section */}
        <div className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm mb-6" data-testid="documents-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold">Mes documents</h2>
            <div>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic" data-testid="file-input" />
              <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}
                disabled={uploading} className="rounded-full" data-testid="upload-doc-btn">
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Envoi...' : 'Ajouter un document'}
              </Button>
            </div>
          </div>
          {documents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <File className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm">Aucun document. Ajoutez vos pièces jointes (ordonnances, devis, etc.).</p>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.map(doc => (
                <div key={doc.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/30" data-testid={`doc-item-${doc.id}`}>
                  <FileText className="w-5 h-5 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{doc.original_filename}</p>
                    <p className="text-xs text-muted-foreground">{formatSize(doc.size)} · {new Date(doc.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => downloadDoc(doc.id)} className="h-8 w-8 p-0" data-testid={`doc-download-${doc.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteDocument(doc.id, doc.original_filename)} className="h-8 w-8 p-0" data-testid={`doc-delete-${doc.id}`}>
                      <Trash2 className="w-4 h-4 text-destructive/70" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bookings section */}
        {bookings.length > 0 && (
          <div className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm mb-6" data-testid="caregiver-bookings">
            <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-primary" /> Mes séjours planifiés
            </h2>
            <div className="space-y-3">
              {bookings.map(b => {
                const statusColors = {
                  pending: 'bg-orange-100 text-orange-700', confirmed: 'bg-blue-100 text-blue-700',
                  in_progress: 'bg-teal-100 text-teal-700', completed: 'bg-green-100 text-green-700',
                  cancelled: 'bg-gray-100 text-gray-500'
                };
                const statusLabels = {
                  pending: 'En attente', confirmed: 'Confirmé', in_progress: 'En cours',
                  completed: 'Terminé', cancelled: 'Annulé'
                };
                return (
                  <div key={b.id} className="flex items-center gap-4 p-4 rounded-xl bg-muted/30 border border-border/30" data-testid={`caregiver-booking-${b.id}`}>
                    <CalendarDays className="w-8 h-8 text-primary/40 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{b.partner_name}</span>
                        <Badge className={`text-xs ${statusColors[b.status]}`}>{statusLabels[b.status]}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(b.start_date).toLocaleDateString('fr-FR')} → {new Date(b.end_date).toLocaleDateString('fr-FR')}
                      </p>
                      {b.notes && <p className="text-xs text-muted-foreground italic mt-0.5">{b.notes}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Requests */}
        <h2 className="font-heading text-xl font-semibold mb-4">Mes demandes de séjour</h2>
        {requests.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center border border-border/50 shadow-sm">
            <Heart className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-heading text-lg font-medium mb-2">Aucune demande pour le moment</h3>
            <p className="text-muted-foreground mb-4">Déposez votre première demande de séjour de répit.</p>
            <Link to="/demander-un-sejour">
              <Button className="rounded-full bg-accent text-accent-foreground">
                Demander un séjour <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map(req => (
              <div key={req.id} className="bg-white rounded-xl p-5 border border-border/50 shadow-sm" data-testid={`request-card-${req.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className={`text-xs ${STATUS_COLORS[req.status]}`}>{STATUS_LABELS[req.status] || req.status}</Badge>
                      {req.location_preference && <span className="text-sm text-muted-foreground">{req.location_preference}</span>}
                    </div>
                    {req.preferred_period && <p className="text-sm text-muted-foreground">Période : {req.preferred_period}</p>}
                    {req.mobility_needs && <p className="text-sm text-muted-foreground">Mobilité : {req.mobility_needs}</p>}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    {new Date(req.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
